import React, { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form';
import update from 'immutability-helper'
import Select from 'react-select'
import styles from './IdCardForm.module.scss'
import { CompactPicker } from 'react-color';
import Column from "../../models/Column";
import Font from '../../models/Font';
import trash from '../../assets/trash.png';
import Image from "../Image";
import useBackgrounds from "../../hooks/useBackgrounds";

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

const ALIGN = ['left', 'center', 'right'];

export default function FieldInput({ field, fields, setFields, template, packageId }) {
  const [values, setValues] = useState({})
  const [fonts, setFonts] = useState([])
  const [alignments, setAlignments] = useState([])
  const [formattedBackgrounds, setFormattedBackgrounds] = useState([])
  const [displayColorPicker, setDisplayColorPicker] = useState(false)
  const { data: backgrounds } = useBackgrounds(packageId);
  const form = useRef(null)
  const { handleSubmit } = useForm();

  useEffect(() => {
    const { left, top, width, height, spacing, textSize, color, fontId, align, text, wrap, sampleText, png, customized, backgroundId, daysOffset } = field

    setValues({ left, top, width, height, spacing, textSize, color, fontId, align, text, wrap, sampleText, png, customized, backgroundId, daysOffset })
  }, [JSON.stringify(field)])

  useEffect(() => {
    Font
      .order({ name: 'asc' })
      .per(100)
      .all()
      .then((res) => {
        const fontsData = res.data.map((e) => ({ label: e.name, value: e.id }))
        setFonts(fontsData)
      })
  }, [])

  useEffect(() => {
    const formatted = backgrounds.map((e) => ({ label: e.color, value: e.id }))

    setFormattedBackgrounds(formatted)
  }, [backgrounds])

  useEffect(() => {
    setAlignments(ALIGN.map((align) => ({ label: align, value: align })))
  }, [])

  const handleColorPickerClick = () => {
    setDisplayColorPicker(state => !state)
  }

  const handleColorPickerClose = () => {
    setDisplayColorPicker(false);
  }

  const handleColorChange = (color) => {
    field.object.color = color.hex;
    field.object.save().then(() => {
      setValues(prevState => ({ ...prevState, color: color.hex }))

      setFields(
        update(fields, {
          [field.id]: {
            $merge: { color: color.hex },
          },
        }),
      )
    })
  }

  const handleFontChange = (value) => {
    field.object.fontId = parseInt(value.value);
    field.object.save().then(() => {
      setValues(prevState => ({ ...prevState, fontId: parseInt(value.value) }))

      setFields(
        update(fields, {
          [field.id]: {
            $merge: { fontId: value.value, fontName: value.label },
          },
        }),
      )
    })
  }

  const handleBackgroundChange = (value) => {
    field.object.backgroundId = parseInt(value.value);
    field.object.save().then(() => {
      setValues(prevState => ({ ...prevState, backgroundId: parseInt(value.value) }))

      setFields(
        update(fields, {
          [field.id]: {
            $merge: { backgroundId: value.value, backgroundName: value.label },
          },
        }),
      )
    })
  }

  const onFileChange = () => {
    const formData = new FormData(form.current)

    fetch(`/templates/${field.object.templateId}/fields/${field.id}/update_personalization_box`,
      { method: 'PATCH', body: formData })
  }

  const onLogoFileChange = (e) => {
    const formData = new FormData()
    formData.append('schoolLogo', e.target.files[0])

    fetch(`/templates/${field.object.templateId}/fields/${field.id}/update_school_logo`,
      { method: 'PATCH', body: formData })
  }

  const handleAlignChange = (value) => {
    field.object.align = value.value;
    field.object.save().then(() => {
      setValues(prevState => ({ ...prevState, align: value.value }))

      setFields(
        update(fields, {
          [field.id]: {
            $merge: { align: value.value },
          },
        }),
      )
    })
  }

  const deleteProperty = ({ [key]: _, ...newObj }, key) => newObj;

  const handleDelete = () => {
    field.object.destroy().then(() => {
      setFields(
        deleteProperty(fields, field.id)
      )
    })
  }

  // manually update field values
  const onChange = (e) => {
    // if (!e.target.value) return;

    const parsedValue = e.target.name === 'text' || e.target.name === 'sampleText' ? e.target.value : parseInt(e.target.value, 10)
    let newValue = Number.isNaN(parsedValue) ? '' : parsedValue
    newValue = e.target.name === 'wrap' ? !field.object.wrap : newValue;
    newValue = e.target.name === 'png' ? !field.object.png : newValue;
    newValue = e.target.name === 'customized' ? !field.object.customized : newValue;

    const newValues = { ...values, [e.target.name]: newValue }
    field.object.x = newValues.left;
    field.object.y = newValues.top;
    field.object.height = newValues.height;
    field.object.width = newValues.width;
    field.object.spacing = newValues.spacing;
    field.object.textSize = newValues.textSize;
    field.object.text = newValues.text;
    field.object.wrap = newValues.wrap;
    field.object.sampleText = newValues.sampleText;
    field.object.png = newValues.png;
    field.object.customized = newValues.customized;
    field.object.daysOffset = newValues.daysOffset;

    if (!e.target.value && e.target.name !== 'wrap') {
      setValues(newValues)
    } else {
      setValues(newValues)

      setFields(
        update(fields, {
          [field.id]: {
            $merge: newValues,
          },
        }),
      )

      field.object.save().then(() => {
      })
    }
  }

  return (
    <div>
      <div className={styles.fieldRow}>
        <div className={styles.columnTitle}>{field.label}</div>
        <img className={styles.imgIcon}
             src={trash}
             alt='remove icon'
             onClick={handleDelete}
        />

        <div className={styles.fieldValues}>
          {Object.keys(values).filter((value) => !['color', 'fontId', 'align', 'text', 'wrap', 'sampleText', 'png', 'customized', 'background', 'backgroundId', 'daysOffset'].includes(value)).map((key) => (
            <label key={key} className={styles.fieldLabel}>
              <span>{capitalizeFirstLetter(key)}</span>
              <input
                name={key}
                type="number"
                value={values[key] || ''}
                onChange={onChange}
                className={styles.input} />
            </label>
          ))}

          <label className={styles.fieldLabel}>
            <span>Color</span>
            <div className={styles.fieldValues}>
              <div className={styles.swatch} onClick={handleColorPickerClick}>
                <div className={ styles.color } style={{ background: values.color }} />
              </div>

              {displayColorPicker && (
                <div className={ styles.popover }>
                  <div className={ styles.cover } onClick={handleColorPickerClose}/>
                  <CompactPicker color={values.color} onChange={handleColorChange} />
                </div>
              )}
            </div>
          </label>

          <label className={styles.fieldLabelSelectAlign}>
            <span>Align</span>

            {alignments.length && (
              <Select
                defaultValue={values.align && alignments.find((align) => align.value === values.align)}
                placeholder="Alignment"
                options={alignments}
                width="80px"
                onChange={handleAlignChange}
              />
            )}

          </label>

          <label className={styles.fieldLabelSelect}>
            <span>Font</span>

            {fonts.length > 0 && (
              <Select
                defaultValue={values.fontId && fonts.find((font) => parseInt(font.value) === parseInt(values.fontId))}
                placeholder="Font"
                options={fonts}
                width="150px"
                onChange={handleFontChange}
              />
            )}
          </label>

          <label className={styles.checkboxLabel}>
            <span>Wrap</span>

            <input
              name="wrap"
              type="checkbox"
              checked={values['wrap'] || false}
              value={values['wrap'] || false}
              onChange={onChange}
              className={styles.checkbox} />
          </label>

          {field.columnType === 'image_if_present' && (
            <label className={styles.checkboxLabel}>
              <span>PNG</span>

              <input
                name="png"
                type="checkbox"
                checked={values['png'] || false}
                value={values['png'] || false}
                onChange={onChange}
                className={styles.checkbox} />
            </label>
          )}

          {(template?.category === 'soft_yearbook_cover' || template?.category === 'hard_yearbook_cover') && (
            <label className={styles.checkboxLabel}>
              <span>Only for customized YB</span>

              <input
                name="customized"
                type="checkbox"
                checked={values['customized'] || false}
                value={values['customized'] || false}
                onChange={onChange}
                className={styles.checkbox} />
            </label>
          )}

          {field.columnType === 'image_if_present' && (
            <label className={styles.fieldLabelSelect}>
              <span>Background</span>

              {formattedBackgrounds.length > 0 && (
                <Select
                  defaultValue={values.backgroundId && formattedBackgrounds.find((background) => parseInt(background.value) === parseInt(values.backgroundId))}
                  placeholder="Background"
                  options={formattedBackgrounds}
                  width="150px"
                  onChange={handleBackgroundChange}
                />
              )}
            </label>
          )}

          {field.column_type !== 'personalization_box' && (
            <label className={styles.fieldLabel}>
              <span>Sample Text</span>
              <input
                name="sampleText"
                value={values['sampleText'] || ''}
                onChange={onChange}
                className={`${styles.input} ${styles.inputText}`} />
            </label>
          )}

          {field.columnType === 'static_text' && (
            <label className={styles.fieldLabel}>
              <span>Text</span>
              <input
                name="text"
                value={values['text'] || ''}
                onChange={onChange}
                className={`${styles.input} ${styles.inputText}`} />
            </label>
          )}

          {field.columnType?.startsWith('expiration_date_') && (
            // "Expires ..." lines render date == job_date + N days. Blank until
            // this is set (per feature spec) so a freshly-placed field can't
            // start printing "Expires <job_date>" before the designer decides.
            <label className={styles.fieldLabel}>
              <span>Days after job date</span>
              <input
                name="daysOffset"
                type="number"
                min="0"
                value={values['daysOffset'] ?? ''}
                onChange={onChange}
                className={styles.input} />
            </label>
          )}

          {field.columnType === 'personalization_box' && (
            <form ref={form} onSubmit={handleSubmit(onFileChange)}>
              <label className={styles.fieldLabelSelectAlign}>
              <span>
                Personalization Box
              </span>

                <input
                  name="personalizationBox"
                  onChange={onFileChange}
                  type="file" />
              </label>
            </form>
          )}

          {field.columnType === 'school_logo' && (
            <label className={styles.fieldLabelSelectAlign}>
              <span>School Logo</span>
              <input
                name="schoolLogo"
                onChange={onLogoFileChange}
                type="file"
                accept="image/*" />
            </label>
          )}
        </div>
      </div>
    </div>
  )
}
